/**
 * Juspay LMS Payment API Service
 * ================================
 * All calls go through the backend (API key never exposed to browser).
 *
 * Endpoints:
 *   POST /api/lms/payment/initiate/     — create Juspay order, returns order_id + merchant_id
 *   POST /api/lms/payment/upi-intent/   — proxy /txns to Juspay, returns sdk_params for QR
 *   GET  /api/lms/payment/status/       — poll order status (CHARGED / AUTHORIZATION_FAILED / PENDING_VBV)
 */

import lmsApi from './api';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type GatewayMode = 'sim' | 'juspay_sandbox' | 'juspay_production';

export interface InitiatePaymentRequest {
    loan_id: string;
    action: 'PAY' | 'CANCEL' | 'FORECLOSE';
    amount: number;
}

/** Response when mode === 'sim' */
export interface SimInitiateResponse {
    mode: 'sim';
    gateway_url: string;
    status: 'ready';
}

/** Response when mode === 'juspay_sandbox' | 'juspay_production' */
export interface JuspayInitiateResponse {
    mode: 'juspay_sandbox' | 'juspay_production';
    order_id: string;
    juspay_order_id: string;
    merchant_id: string;
    amount: number;
    action: string;
    loan_id: string;
    status: 'ready';
}

export type InitiatePaymentResponse = SimInitiateResponse | JuspayInitiateResponse;

export interface UpiSdkParams {
    tr: string;
    tid?: string;
    merchant_vpa: string;
    merchant_name: string;
    mcc?: string;
    amount: string;
}

export interface UpiIntentResponse {
    status: string;           // 'PENDING_VBV'
    txn_id: string;
    order_id: string;
    payment: {
        sdk_params: UpiSdkParams;
        authentication: {
            url: string;
            method: string;
        };
    };
}

export interface PaymentStatusResponse {
    order_id: string;
    /** CHARGED | AUTHORIZATION_FAILED | PENDING_VBV | NEW */
    status: string;
    amount: number;
    txn_id: string;
    loan_id: string | null;
}

// ---------------------------------------------------------------------------
// API calls
// ---------------------------------------------------------------------------

/**
 * Step 1 — Initiate payment.
 *
 * When mode === 'sim': returns gateway_url for the simulation page.
 * When mode === 'juspay_*': creates a Juspay order and returns order_id + merchant_id.
 */
export async function initiatePayment(
    payload: InitiatePaymentRequest,
): Promise<InitiatePaymentResponse> {
    const response = await lmsApi.post<InitiatePaymentResponse>(
        '/payment/initiate/',
        payload,
    );
    return response.data;
}

/**
 * Step 2 — Initiate UPI Intent transaction (Juspay mode only).
 *
 * Proxies the Juspay /txns call server-side.
 * Returns sdk_params used to build the upi:// URI and QR code.
 *
 * @param order_id     The order_id returned by initiatePayment()
 * @param customer_id  Stable customer identifier (phone number)
 * @param upi_app      Optional: package name of UPI app for deep-link
 *                     (e.g. 'com.phonepe.app', 'com.google.android.apps.nbu.paisa.user')
 *                     Leave empty for generic intent (QR / any app).
 */
export async function initiateUpiIntent(
    order_id: string,
    customer_id: string,
    upi_app = '',
): Promise<UpiIntentResponse> {
    const response = await lmsApi.post<UpiIntentResponse>(
        '/payment/upi-intent/',
        { order_id, customer_id, upi_app },
    );
    return response.data;
}

/**
 * Step 3 — Poll payment status.
 *
 * Call every 3 seconds until status is CHARGED or AUTHORIZATION_FAILED.
 * Maximum polling duration: 10 minutes (200 calls × 3s).
 */
export async function getPaymentStatus(order_id: string): Promise<PaymentStatusResponse> {
    const response = await lmsApi.get<PaymentStatusResponse>(
        `/payment/status/?order_id=${encodeURIComponent(order_id)}`,
    );
    return response.data;
}

// ---------------------------------------------------------------------------
// UPI URI builder
// ---------------------------------------------------------------------------

/**
 * Build a upi://pay URI from Juspay sdk_params.
 *
 * Format:
 *   upi://pay?tr=...&tid=...&pa=...&mc=...&pn=...&am=...&cu=INR&tn=...
 *
 * Note: tid is optional per Juspay docs — omit if absent.
 */
export function buildUpiUri(
    sdkParams: UpiSdkParams,
    description = 'Loan Repayment',
): string {
    const params = new URLSearchParams({
        tr: sdkParams.tr,
        pa: sdkParams.merchant_vpa,
        pn: sdkParams.merchant_name,
        am: sdkParams.amount,
        cu: 'INR',
        tn: description,
    });
    if (sdkParams.tid) params.set('tid', sdkParams.tid);
    if (sdkParams.mcc) params.set('mc', sdkParams.mcc);
    return `upi://pay?${params.toString()}`;
}

// ---------------------------------------------------------------------------
// UPI app deep-link configs
// ---------------------------------------------------------------------------

export interface UpiApp {
    name: string;
    /** Android package name — used in upi:// intent */
    packageName: string;
    /** iOS URL scheme */
    iosScheme: string;
    /** Display icon (emoji fallback — replace with actual SVG imports if available) */
    icon: string;
}

export const UPI_APPS: UpiApp[] = [
    {
        name: 'GPay',
        packageName: 'com.google.android.apps.nbu.paisa.user',
        iosScheme: 'gpay',
        icon: '🟢',
    },
    {
        name: 'PhonePe',
        packageName: 'com.phonepe.app',
        iosScheme: 'phonepe',
        icon: '🟣',
    },
    {
        name: 'BHIM',
        packageName: 'in.org.npci.upiapp',
        iosScheme: 'bhim',
        icon: '🟠',
    },
];
